import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { ImageCropper } from '@/widgets/image-cropper';
import { useTranslate } from '@tolgee/react';
import { AlertCircle, Camera, CheckCircle2, Pencil, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

/** Legacy step7: the photo is mandatory ("photo obligatoire et privée") and cropped. */
export interface PhotoStepData {
  photo: File | null;
  croppedPhotoBlob: Blob | null;
  /** resume: a photo already exists server-side */
  alreadyUploaded: boolean;
}

interface StepPhotoProps {
  data: PhotoStepData;
  onChange: (data: Partial<PhotoStepData>) => void;
  onValidChange: (isValid: boolean) => void;
}

export function StepPhoto({ data, onChange, onValidChange }: StepPhotoProps) {
  const { t } = useTranslate();
  const [preview, setPreview] = useState<string | null>(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: onValidChange is stable from parent
  useEffect(() => {
    onValidChange(!!data.croppedPhotoBlob || data.alreadyUploaded);
  }, [data.croppedPhotoBlob, data.alreadyUploaded]);

  useEffect(() => {
    if (data.croppedPhotoBlob) {
      const url = URL.createObjectURL(data.croppedPhotoBlob);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview(null);
  }, [data.croppedPhotoBlob]);

  const handleCropComplete = useCallback(
    (croppedBlob: Blob) => {
      const file = new File([croppedBlob], 'photo.png', { type: 'image/png' });
      onChange({ photo: file, croppedPhotoBlob: croppedBlob });
      setIsCropperOpen(false);
    },
    [onChange],
  );

  const removePhoto = () => onChange({ photo: null, croppedPhotoBlob: null });

  return (
    <div className="space-y-6">
      <Alert className="border-primary/50 bg-primary/5">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{t('signup.hints.photoRequired')}</AlertDescription>
      </Alert>

      {data.alreadyUploaded && !data.croppedPhotoBlob && (
        <Alert className="border-success/40 bg-success/5">
          <CheckCircle2 className="h-4 w-4 text-success" />
          <AlertDescription>{t('signup.photoAlreadyUploaded')}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <Label className="text-base font-medium">
          {t('signup.fields.photo')}
          <span className="text-destructive"> *</span>
        </Label>

        {preview ? (
          <div className="relative w-48 mx-auto">
            <div className="relative aspect-[3/4] overflow-hidden rounded-lg border-2 border-primary">
              <img src={preview} alt="Preview" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -top-2 -right-2 flex gap-1">
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="h-8 w-8 rounded-full shadow-md"
                onClick={() => setIsCropperOpen(true)}
                aria-label={t('signup.cropPhoto')}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="h-8 w-8 rounded-full shadow-md"
                onClick={removePhoto}
                aria-label={t('common.delete', 'Supprimer')}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            data-testid="photo-picker"
            onClick={() => setIsCropperOpen(true)}
            className="relative w-full border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer border-muted-foreground/25 hover:border-primary/50 hover:bg-primary-surface/30"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Camera className="h-8 w-8 text-primary" />
              </div>
              <div>
                <p className="font-medium">{t('signup.hints.clickToSelect')}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('signup.hints.cropEnabled')}
                </p>
              </div>
              <Button type="button" variant="outline" className="mt-2" asChild>
                <span>
                  <Upload className="h-4 w-4 mr-2" />
                  {t('signup.buttons.selectPhoto')}
                </span>
              </Button>
            </div>
          </button>
        )}

        <p className="text-xs text-muted-foreground text-center">{t('signup.hints.photoFormat')}</p>
      </div>

      <div className="rounded-lg bg-muted/50 p-4 space-y-2">
        <h4 className="font-medium text-sm">{t('signup.photoGuidelines.title')}</h4>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>• {t('signup.photoGuidelines.face')}</li>
          <li>• {t('signup.photoGuidelines.recent')}</li>
          <li>• {t('signup.photoGuidelines.quality')}</li>
          <li>• {t('signup.photoGuidelines.alone')}</li>
        </ul>
      </div>

      <ImageCropper
        open={isCropperOpen}
        onClose={() => setIsCropperOpen(false)}
        onCropComplete={handleCropComplete}
        aspectRatio={3 / 4}
        title={t('signup.cropPhoto')}
      />
    </div>
  );
}
